import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { shoppingListApi } from "@/services/shoppingListApi";
import { XIcon } from "lucide-react";
import { ShoppingList } from "@/pages/ShoppingList/types.ts";

interface EditListModalProps {
  isOpen: boolean;
  onClose: () => void;
  list: ShoppingList;
}

export const EditListModal = ({
  isOpen,
  onClose,
  list,
}: EditListModalProps) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: list.name,
    type: list.type,
  });

  // Options pour les types de liste
  const listTypes = [
    "Alimentaire",
    "Outillage",
    "Fournitures",
    "Vêtements",
    "Électronique",
    "Autre",
  ];

  // Mise à jour du formulaire si la liste change
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: list.name,
        type: list.type,
      });
    }
  }, [isOpen, list]);

  // Mutation pour modifier la liste
  const updateListMutation = useMutation({
    mutationFn: (data: { name: string; type: string; notes?: string }) =>
      shoppingListApi.updateList(list._id, data),
    onSuccess: () => {
      // Invalider les queries appropriées
      queryClient.invalidateQueries({ queryKey: ["shopping-list", list._id] });
      queryClient.invalidateQueries({ queryKey: ["shopping-lists"] });
      onClose();
    },
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateListMutation.mutate(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-base-100 rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Modifier la liste</h2>
          <button className="btn btn-ghost btn-sm btn-circle" onClick={onClose}>
            <XIcon size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-control mb-4">
            <label className="label">
              <span className="label-text">Nom de la liste</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="input input-bordered w-full"
              placeholder="Ex: Courses semaine, Bricolage salon..."
              required
            />
          </div>

          <div className="form-control mb-4">
            <label className="label">
              <span className="label-text">Type de liste</span>
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="select select-bordered w-full"
              required
            >
              {listTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Annuler
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={updateListMutation.isPending}
            >
              {updateListMutation.isPending ? (
                <span className="loading loading-spinner loading-sm"></span>
              ) : (
                "Mettre à jour"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
